import {
  Loader2,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Sparkles,
  SquarePen,
} from 'lucide-react'
import * as React from 'react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { CaseFactsDialog, useProject } from '@/modules/projects'
import { captureException } from '@/services/monitoring/captureException'
import { useDialogActions } from '@/stores/dialog'
import { cn } from '@/utils'
import {
  useApplyPendingFilters,
  useApplyPendingSeed,
  useChatChannel,
  useChatMessages,
  useChatMutations,
  useChatSidebarSession,
  useCurrentSession,
  useCurrentSessionId,
  useDiagnosticsEnabled,
  useSessionTokenTotals,
  useSidebarFullScreen,
} from '../../hooks'
import { IS_ADMIN_MODE } from '../../hooks/useIsAdminMode'
import { formatCostMicros } from '../../utils/usageFormat'
import ChatComposer from '../ChatComposer'
import ChatSessionsPopover from '../ChatSessionsPopover'
import { formatTokens } from '../messages/assistantBubble/UsageChip'
import ThreadView from '../ThreadView'
import ActiveFiltersChip from './ActiveFiltersChip'
import CollapseEdge from './CollapseEdge'
import ProcessingNotice from './ProcessingNotice'
import ResizeHandle, { useStartResize } from './ResizeHandle'

interface ExpandedProps {
  slug: string
  width: number
}

const DARK_ICON_BUTTON_CLASSES =
  'text-white/70 hover:bg-white/10 hover:text-white focus-visible:ring-white/40'

interface DarkHeaderIconButtonProps {
  tooltip: string
  ariaLabel: string
  onClick: () => void
  children: React.ReactNode
  // role+ariaChecked together turn the button into a toggle (fullscreen,
  // diagnostics). Without them it renders as a plain button.
  role?: 'switch'
  ariaChecked?: boolean
  activeClassName?: string
}

function DarkHeaderIconButton({
  tooltip,
  ariaLabel,
  onClick,
  children,
  role,
  ariaChecked,
  activeClassName,
}: DarkHeaderIconButtonProps) {
  const isActiveToggle = role === 'switch' && ariaChecked === true
  return (
    <Tooltip content={tooltip}>
      <Button
        type="button"
        theme="ghost"
        size="sm"
        isIcon
        role={role}
        aria-checked={role === 'switch' ? ariaChecked : undefined}
        aria-label={ariaLabel}
        onClick={onClick}
        className={cn(
          isActiveToggle && activeClassName
            ? activeClassName
            : DARK_ICON_BUTTON_CLASSES,
        )}
      >
        {children}
      </Button>
    </Tooltip>
  )
}

function DaystromWordmark() {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-300 via-pink-300 to-violet-500 bg-[length:220%_220%] text-white shadow-[0_0_14px_rgba(56,189,248,0.55)] ring-1 ring-white/40 animate-daystrom-shine motion-reduce:animate-none">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-md bg-white/30 blur-[1px]"
        />
        <Sparkles
          className="relative h-4 w-4 drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]"
          strokeWidth={2.5}
        />
      </span>
      <span className="truncate text-base font-semibold tracking-tight text-white">
        Daystrom
        <span className="ml-0.5 text-sm font-medium text-white/60">™</span>
      </span>
    </span>
  )
}

export default function Expanded({ slug, width }: ExpandedProps) {
  useApplyPendingFilters()
  useApplyPendingSeed(slug)
  useChatChannel(slug)
  const sessionId = useCurrentSessionId()
  const session = useCurrentSession()
  const messages = useChatMessages()
  const project = useProject()
  const diagnosticsEnabled = useDiagnosticsEnabled()
  const fullScreen = useSidebarFullScreen()
  const tokenTotals = useSessionTokenTotals()
  const { setSidebarCollapsed, setSidebarWidth, setSidebarFullScreen } =
    useChatMutations()
  const { errorToast } = useToast()
  const { openDialog } = useDialogActions()
  const [exporting, setExporting] = React.useState(false)

  const handleExport = React.useCallback(
    async (format: 'pdf' | 'docx') => {
      if (!session || exporting) return
      setExporting(true)
      try {
        // Lazy imports: jspdf / docx and the chart capture pipeline are heavy
        // and only needed when someone actually exports.
        const args = { session, messages, projectName: project?.name ?? null }
        if (format === 'pdf') {
          const { exportChatSessionToPDF } = await import(
            '../../utils/exportChatPdf'
          )
          await exportChatSessionToPDF(args)
        } else {
          const { exportChatSessionToDOCX } = await import(
            '../../utils/exportChatDocx'
          )
          await exportChatSessionToDOCX(args)
        }
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        )
        const target = format === 'pdf' ? 'PDF' : 'Word'
        errorToast('Export failed', `Could not export this chat to ${target}.`)
      } finally {
        setExporting(false)
      }
    },
    [session, messages, project?.name, exporting, errorToast],
  )
  const {
    selectSession,
    clearSession,
    createAndOpenSession,
    creatingSession,
    createFailed,
  } = useChatSidebarSession(slug)

  const startResize = useStartResize(width, setSidebarWidth)
  const hasUsage =
    diagnosticsEnabled &&
    (tokenTotals.inputTokens > 0 || tokenTotals.outputTokens > 0)

  // Escape exits fullscreen rather than closing the chat entirely; matches
  // the behavior of expanded artifact dialogs elsewhere in the app.
  React.useEffect(() => {
    if (!fullScreen) return
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setSidebarFullScreen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [fullScreen, setSidebarFullScreen])

  return (
    <aside
      className={cn(
        'flex flex-col bg-background animate-drawer-in motion-reduce:animate-none',
        fullScreen
          ? 'fixed inset-0 z-50'
          : [
              // mobile: full-height overlay anchored to the right edge
              'fixed inset-y-0 right-0 z-40 max-w-full shadow-2xl',
              // desktop (xl+): inline column sharing the layout row with main content
              'xl:relative xl:right-auto xl:z-auto xl:h-full xl:shrink-0 xl:border-l xl:shadow-none',
            ],
      )}
      style={fullScreen ? undefined : { width }}
    >
      {!fullScreen && <ResizeHandle onPointerDown={startResize} />}
      <header className="relative z-10 border-b border-blue-7/40 bg-gradient-to-r from-blue-7 via-indigo-800 to-cyan-700 text-white">
        <div className="mx-auto flex w-full max-w-5xl items-center gap-1.5 px-6 py-2">
          <DaystromWordmark />

          <div className="ml-auto flex shrink-0 items-center gap-1">
            <DarkHeaderIconButton
              tooltip="New chat"
              ariaLabel="New chat"
              onClick={() => {
                void createAndOpenSession()
              }}
            >
              <SquarePen />
            </DarkHeaderIconButton>
            <ChatSessionsPopover
              slug={slug}
              onSelect={selectSession}
              onCreateNew={createAndOpenSession}
              onArchiveCurrent={clearSession}
              triggerClassName={DARK_ICON_BUTTON_CLASSES}
            />
            <DarkHeaderIconButton
              tooltip={fullScreen ? 'Exit full screen' : 'Full screen'}
              ariaLabel={fullScreen ? 'Exit full screen' : 'Full screen'}
              role="switch"
              ariaChecked={fullScreen}
              onClick={() => setSidebarFullScreen(!fullScreen)}
            >
              {fullScreen ? <Minimize2 /> : <Maximize2 />}
            </DarkHeaderIconButton>
            <DropdownMenu>
              <Tooltip content="More">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    theme="ghost"
                    size="sm"
                    isIcon
                    aria-label="More actions"
                    className={DARK_ICON_BUTTON_CLASSES}
                  >
                    {exporting ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <MoreHorizontal />
                    )}
                  </Button>
                </DropdownMenuTrigger>
              </Tooltip>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => openDialog(CaseFactsDialog.id)}
                >
                  Case facts
                </DropdownMenuItem>
                {session && messages.length > 0 && (
                  <>
                    <DropdownMenuItem
                      onSelect={() => {
                        void handleExport('pdf')
                      }}
                    >
                      Export as PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        void handleExport('docx')
                      }}
                    >
                      Export as Word (.docx)
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {!fullScreen && (
          <CollapseEdge onClick={() => setSidebarCollapsed(true)} />
        )}
      </header>

      {hasUsage && (
        <div className="border-b bg-muted/20">
          <div className="mx-auto flex w-full max-w-5xl items-center justify-end px-6 py-1.5">
            <span className="truncate text-[11px] tabular-nums text-muted-foreground">
              {formatTokens(tokenTotals.inputTokens)} in ·{' '}
              {formatTokens(tokenTotals.outputTokens)} out
              {IS_ADMIN_MODE &&
                tokenTotals.costMicros > 0 &&
                ` · ${formatCostMicros(tokenTotals.costMicros)}`}
            </span>
          </div>
        </div>
      )}

      <ProcessingNotice />

      <ActiveFiltersChip />

      <div className="flex-1 overflow-hidden">
        <ThreadView
          slug={slug}
          onCreateNew={createAndOpenSession}
          creating={creatingSession}
          createFailed={createFailed}
        />
      </div>

      <ChatComposer key={sessionId ?? 'no-session'} slug={slug} />
    </aside>
  )
}
